import { zodResolver } from '@hookform/resolvers/zod';
import { Avatar, Box, Button, Card, Grid, Group, PasswordInput, Stack, Text, Title } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../components/Toast';
import { tr } from '../lib/i18n';

const Schema = z
  .object({
    currentPassword: z.string().min(1, tr('مطلوب', 'Required')),
    newPassword: z.string().min(8, tr('8 أحرف على الأقل', 'At least 8 characters')).refine((p) => /[A-Za-z]/.test(p) && /\d/.test(p), tr('يجب أن تحتوي حروفاً وأرقاماً', 'Must contain letters and numbers')),
    confirm: z.string(),
  })
  .refine((v) => v.newPassword === v.confirm, { path: ['confirm'], message: tr('غير متطابقة', 'Passwords do not match') });

export function ProfilePage() {
  const { me, logout } = useAuth();
  const { toast, toastError } = useToast();
  const { register, handleSubmit, formState, setError } = useForm<z.infer<typeof Schema>>({ resolver: zodResolver(Schema) });
  const submit = handleSubmit(async (v) => {
    try {
      await api('/auth/change-password', { method: 'POST', body: { currentPassword: v.currentPassword, newPassword: v.newPassword } });
      toast(tr('تم تغيير كلمة المرور — سجّل الدخول مجدداً', 'Password changed — please sign in again'));
      await logout();
    } catch (e) {
      if (e instanceof ApiError && e.fieldErrors().currentPassword) setError('currentPassword', { message: e.message });
      else toastError(e);
    }
  });
  return (
    <Grid gap="lg" maw={960}>
      <Grid.Col span={{ base: 12, md: 4 }}>
        <Card>
          <Stack align="center" gap={6} py="sm">
            <Avatar size={64} radius="xl" color="ink" variant="filled">{me?.fullName?.[0] ?? '؟'}</Avatar>
            <Title order={3} fz="md" mt="xs">{me?.fullName}</Title>
            <Text size="sm" c="dimmed" dir="ltr">{me?.username}</Text>
            <Text size="xs" c="dimmed" mt={4}>{tr('مدير المنصة', 'Platform admin')}</Text>
          </Stack>
        </Card>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 8 }}>
        <Card>
          <Group gap="sm" mb="lg" wrap="nowrap">
            <IconLock size={20} stroke={1.6} />
            <Box>
              <Title order={3} fz="md">{tr('تغيير كلمة المرور', 'Change password')}</Title>
              <Text size="sm" c="dimmed">{tr('بعد الحفظ ستخرج من الجلسة وتدخل بكلمة المرور الجديدة.', 'After saving you will be signed out and sign in with the new password.')}</Text>
            </Box>
          </Group>
          <form onSubmit={submit} noValidate>
            <Stack gap="md">
              <PasswordInput label={tr('كلمة المرور الحالية', 'Current password')} autoComplete="current-password" dir="ltr" {...register('currentPassword')} error={formState.errors.currentPassword?.message} />
              <PasswordInput label={tr('كلمة المرور الجديدة', 'New password')} description={tr('8 أحرف على الأقل، حروف وأرقام', 'At least 8 characters, letters and numbers')} autoComplete="new-password" dir="ltr" {...register('newPassword')} error={formState.errors.newPassword?.message} />
              <PasswordInput label={tr('تأكيد كلمة المرور', 'Confirm password')} autoComplete="new-password" dir="ltr" {...register('confirm')} error={formState.errors.confirm?.message} />
              <Group justify="flex-end"><Button type="submit" loading={formState.isSubmitting}>{tr('حفظ كلمة المرور', 'Save password')}</Button></Group>
            </Stack>
          </form>
        </Card>
      </Grid.Col>
    </Grid>
  );
}
